import React from 'react';
import { Helmet } from 'react-helmet';
import { ImWhatsapp } from 'react-icons/im';
import {
  FiMapPin,
  FiCalendar,
  FiCheck,
  FiAlertCircle,
  FiZap,
  FiDroplet,
  FiSearch,
  FiTool,
  FiDisc,
  FiSun,
  FiMonitor,
  FiCamera,
} from 'react-icons/fi';

import {
  Container,
  FloatingButtons,
  Hero,
  GeoSeo,
  ServicesGrid,
  TechnologySection,
  AuthoritySection,
  FaqSection,
  CtaFinal,
} from './styles';

import whatsAppSVG from '~/assets/icons/whatsapp.svg';
import bgHeroUrl from '~/assets/banners/bg-hero.jpg';

const WA_NUMBER = '551141981987';
const WA_MESSAGE = encodeURIComponent(
  'Olá! Gostaria de agendar uma avaliação odontológica na COI Prime.'
);
const WHATSAPP_LINK = `https://api.whatsapp.com/send?phone=${WA_NUMBER}&text=${WA_MESSAGE}`;
const MAPS_LINK = 'https://maps.app.goo.gl/LPv5rTrzvAVKhsVSA';

const SITE_URL = 'https://coiprime.com.br';
const PAGE_URL = `${SITE_URL}/dentistabarueri`;
const BUSINESS_ID = `${SITE_URL}/#organization`;
const PHONE_E164 = '+551141981987';
const PHONE_LABEL = '(11) 4198-1987';
const STREET_ADDRESS = 'Av. Trindade, 165 - Bethaville I';
const POSTAL_CODE = '06404-326';
const PAGE_TITLE = 'Dentista em Bethaville, Barueri | COI Prime Odontologia';
const PAGE_DESCRIPTION =
  'Dentista em Bethaville, Barueri: avaliação odontológica completa com RX panorâmico, escaneamento 3D e câmera intraoral na COI Prime. Av. Trindade, 165. Agende.';

const SERVICES = [
  {
    icon: <FiAlertCircle size={26} />,
    title: 'Dor de Dente',
    description:
      'Diagnóstico rápido e preciso para identificar a causa da dor.',
  },
  {
    icon: <FiZap size={26} />,
    title: 'Dente Quebrado',
    description:
      'Avaliação imediata para restaurar função e estética do sorriso.',
  },
  {
    icon: <FiDroplet size={26} />,
    title: 'Limpeza Dental',
    description:
      'Prevenção e remoção de placa bacteriana para manter a saúde bucal.',
  },
  {
    icon: <FiSearch size={26} />,
    title: 'Avaliação Odontológica',
    description:
      'Check-up completo com exames digitais e planejamento preciso.',
  },
  {
    icon: <FiTool size={26} />,
    title: 'Restauração Dentária',
    description: 'Recuperação de dentes comprometidos com estética natural.',
  },
  {
    icon: <FiDisc size={26} />,
    title: 'Implante Dentário',
    description: 'Reposição dentária com planejamento digital e mais precisão.',
  },
  {
    icon: <FiSun size={26} />,
    title: 'Clareamento Dental',
    description:
      'Sorriso mais branco com segurança e acompanhamento profissional.',
  },
];

// Perguntas diretas (AEO): mesmo conteúdo renderizado na página e no JSON-LD
const FAQS: { question: string; answer: string }[] = [
  {
    question: 'Onde fazer implante dentário no Bethaville?',
    answer:
      'A COI Prime faz implante dentário no Bethaville, em Barueri, na Av. Trindade, 165 - Bethaville I. O planejamento é feito na própria clínica, com RX panorâmico digital e escaneamento intraoral 3D, para mais precisão e previsibilidade.',
  },
  {
    question: 'Onde fazer limpeza dental e clareamento no Bethaville, Barueri?',
    answer:
      'Na COI Prime, clínica odontológica no Bethaville, a limpeza dental e o clareamento são feitos após avaliação e com acompanhamento profissional, na Av. Trindade, 165 - Bethaville I, Barueri.',
  },
  {
    question: 'Como escolher um dentista no Bethaville, Barueri?',
    answer:
      'Procure uma clínica que faça avaliação completa com exames de imagem e tenha experiência clínica. A equipe da COI Prime tem mais de 30 anos de experiência e realiza RX panorâmico, escaneamento 3D e câmera intraoral no mesmo local.',
  },
  {
    question: 'O que fazer em caso de dor de dente em Barueri?',
    answer:
      'Procure um dentista o quanto antes. A dor pode estar ligada a cárie, inflamação, fratura ou problema gengival, e só uma avaliação com exames de imagem identifica a causa. A COI Prime faz esse diagnóstico no Bethaville.',
  },
  {
    question: 'O que fazer quando quebra um dente?',
    answer:
      'O dente quebrado deve ser avaliado rapidamente para evitar dor, infiltrações e agravamento do problema. Evite mastigar do lado afetado até a consulta.',
  },
  {
    question: 'A limpeza dental dói?',
    answer:
      'Na maioria dos casos, a limpeza dental é confortável e ajuda na prevenção de cáries e problemas gengivais.',
  },
  {
    question: 'Onde fazer RX panorâmico em Barueri? Preciso dele na avaliação?',
    answer:
      'O RX panorâmico ajuda no diagnóstico preciso e identifica problemas que muitas vezes não são visíveis clinicamente. Na COI Prime, no Bethaville, ele é feito na própria clínica.',
  },
  {
    question: 'O clareamento dental enfraquece os dentes?',
    answer:
      'Quando realizado com acompanhamento profissional, o clareamento dental é seguro e não enfraquece os dentes.',
  },
  {
    question: 'A COI Prime atende convênios e planos de saúde?',
    answer:
      'Não. A COI Prime não atende convênios nem planos de saúde. O atendimento é particular, com avaliação completa e planejamento individual.',
  },
  {
    question:
      'Qual o endereço e o telefone da COI Prime no Bethaville, Barueri?',
    answer: `A COI Prime fica na ${STREET_ADDRESS}, Barueri - SP, CEP ${POSTAL_CODE}. Telefone e WhatsApp: ${PHONE_LABEL}.`,
  },
];

const schemaMarkup = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': ['Dentist', 'MedicalClinic', 'LocalBusiness'],
      '@id': BUSINESS_ID,
      name: 'COI Prime Odontologia',
      alternateName: 'COI Prime',
      url: SITE_URL,
      description: PAGE_DESCRIPTION,
      address: {
        '@type': 'PostalAddress',
        streetAddress: STREET_ADDRESS,
        addressLocality: 'Barueri',
        addressRegion: 'SP',
        postalCode: POSTAL_CODE,
        addressCountry: 'BR',
      },
      telephone: PHONE_E164,
      email: 'contato@coiprime.com.br',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: PHONE_E164,
        contactType: 'customer service',
        areaServed: 'BR',
        availableLanguage: 'pt-BR',
      },
      priceRange: '$$',
      hasMap: MAPS_LINK,
      sameAs: [
        'https://www.instagram.com/coiprime/',
        'https://www.facebook.com/coi.prime',
      ],
      areaServed: [
        { '@type': 'City', name: 'Barueri' },
        { '@type': 'Place', name: 'Bethaville, Barueri - SP' },
      ],
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '08:00',
          closes: '19:00',
        },
      ],
      medicalSpecialty: [
        'Dentistry',
        'Endodontics',
        'Orthodontics',
        'Prosthodontics',
      ],
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Tratamentos odontológicos no Bethaville, Barueri',
        itemListElement: SERVICES.map((service) => ({
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: service.title,
            description: service.description,
          },
        })),
      },
    },
    {
      '@type': 'WebPage',
      '@id': `${PAGE_URL}#webpage`,
      url: PAGE_URL,
      name: PAGE_TITLE,
      description: PAGE_DESCRIPTION,
      inLanguage: 'pt-BR',
      about: { '@id': BUSINESS_ID },
    },
    {
      '@type': 'FAQPage',
      '@id': `${PAGE_URL}#faq`,
      isPartOf: { '@id': `${PAGE_URL}#webpage` },
      mainEntity: FAQS.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    },
  ],
};

const DentistaBarueri: React.FC = () => {
  return (
    <Container>
      <Helmet htmlAttributes={{ lang: 'pt-BR' }}>
        <title>{PAGE_TITLE}</title>
        <meta httpEquiv="Content-Type" content="text/html;charset=UTF-8" />
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta
          name="robots"
          content="index, follow, max-snippet:-1, max-image-preview:large"
        />
        <meta name="geo.region" content="BR-SP" />
        <meta name="geo.placename" content="Bethaville, Barueri" />
        {/* Preload LCP hero image — browser discovers it immediately, before CSS */}
        <link rel="preload" as="image" href={bgHeroUrl} />

        {/* Preconnect para domínios externos usados na página */}
        <link rel="preconnect" href="https://api.whatsapp.com" />
        <link rel="dns-prefetch" href="https://api.whatsapp.com" />
        <link rel="preconnect" href="https://maps.app.goo.gl" />
        <link rel="dns-prefetch" href="https://maps.app.goo.gl" />

        <link rel="canonical" href={PAGE_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="pt_BR" />
        <meta property="og:site_name" content="COI Prime Odontologia" />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESCRIPTION} />
        <script type="application/ld+json">
          {JSON.stringify(schemaMarkup)}
        </script>
      </Helmet>

      {/* FLOATING BUTTONS */}
      <FloatingButtons aria-label="Botões de contato rápido">
        <a
          href={WHATSAPP_LINK}
          rel="noopener noreferrer"
          target="_blank"
          className="fab-whatsapp"
          title="WhatsApp"
          aria-label="Fale pelo WhatsApp"
        >
          <ImWhatsapp size={24} color="#fff" />
        </a>
        <a
          href={MAPS_LINK}
          rel="noopener noreferrer"
          target="_blank"
          className="fab-maps"
          title="Ver no Google Maps"
          aria-label="Ver no Google Maps"
        >
          <FiMapPin size={22} color="#fff" />
        </a>
        <a
          href={WHATSAPP_LINK}
          rel="noopener noreferrer"
          target="_blank"
          className="fab-schedule"
          title="Agendar Avaliação"
          aria-label="Agendar Avaliação"
        >
          <FiCalendar size={22} color="#fff" />
        </a>
      </FloatingButtons>

      {/* ── HERO ── */}
      <Hero>
        <div className="container">
          <div className="row justify-content-start">
            <div className="col-lg-7 pt-5 mt-5 pb-5">
              <h1
                className="text-white fw-semibold mb-3"
                style={{
                  fontSize: 'clamp(1.85rem, 4vw, 3.25rem)',
                  lineHeight: 1.15,
                }}
              >
                Dentista em Bethaville, Barueri com Avaliação Completa e
                Tecnologia Avançada
              </h1>
              <p
                className="text-white fs-5 mb-5"
                style={{ opacity: 0.88, maxWidth: 520 }}
              >
                Clínica odontológica no Bethaville com diagnóstico preciso para
                dor de dente, dente quebrado, limpeza, restauração, implantes e
                clareamento.
              </p>
              <a
                href={WHATSAPP_LINK}
                rel="noopener noreferrer"
                target="_blank"
                className="hero-cta"
                title="Agendar Avaliação"
              >
                Agendar Avaliação <img src={whatsAppSVG} width={22} alt="" />
              </a>
              <small
                className="d-block text-white mt-3"
                style={{ opacity: 0.6, fontSize: '0.8125rem' }}
              >
                *Não atendemos convênios e Planos de Saúde
              </small>
            </div>
          </div>
        </div>
      </Hero>

      {/* ── GEO + SEO ── */}
      <GeoSeo>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-9">
              <h2>
                Clínica Odontológica no Bethaville, Barueri: Dor de Dente,
                Limpeza e Avaliação Completa
              </h2>
              <p>
                Se você procura dentista no Bethaville, em Barueri, para dor de
                dente, dente quebrado, limpeza dental, restauração, implante ou
                clareamento, a COI Prime é uma clínica odontológica no
                Bethaville com mais de 30 anos de experiência, tecnologia de
                ponta e diagnóstico preciso.
              </p>
              <p>
                Nossa clínica, na Av. Trindade, 165 - Bethaville I, realiza
                todos os exames no mesmo local, sem precisar sair para fazer RX
                panorâmico ou escaneamento digital.
              </p>
            </div>
          </div>
        </div>
      </GeoSeo>

      {/* ── SERVICES ── */}
      <ServicesGrid>
        <div className="container">
          <h2>
            Tratamentos Odontológicos Mais Procurados no Bethaville, Barueri
          </h2>
          <div className="row g-4">
            {SERVICES.map((s) => (
              <div key={s.title} className="col-md-6 col-lg-4">
                <div className="service-card">
                  <div className="icon-wrap">{s.icon}</div>
                  <h3>{s.title}</h3>
                  <p>{s.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </ServicesGrid>

      {/* ── TECHNOLOGY ── */}
      <TechnologySection>
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-lg-7">
              <h2>Exames Odontológicos na Própria Clínica, no Bethaville</h2>
              <p className="lead-text">
                A tecnologia de ponta utilizada na COI Prime, em Barueri,
                permite um diagnóstico mais preciso e seguro.
              </p>
              <ul className="tech-list">
                <li>
                  <FiMonitor size={20} className="tech-icon" />
                  RX panorâmico digital
                </li>
                <li>
                  <FiSearch size={20} className="tech-icon" />
                  Escaneamento intraoral 3D
                </li>
                <li>
                  <FiCamera size={20} className="tech-icon" />
                  Câmera intraoral
                </li>
              </ul>
              <p className="tech-footer">
                Isso proporciona mais previsibilidade no planejamento do
                tratamento e maior segurança para o paciente.
              </p>
            </div>
            <div className="col-lg-5">
              <div className="tech-badges">
                <div className="tech-badge">
                  <p className="badge-label">Diagnóstico</p>
                  <span className="badge-value">Preciso</span>
                </div>
                <div className="tech-badge">
                  <p className="badge-label">RX</p>
                  <span className="badge-value">Digital</span>
                </div>
                <div className="tech-badge">
                  <p className="badge-label">Scanner</p>
                  <span className="badge-value">3D</span>
                </div>
                <div className="tech-badge">
                  <p className="badge-label">Câmera</p>
                  <span className="badge-value">Intraoral</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </TechnologySection>

      {/* ── AUTHORITY ── */}
      <AuthoritySection>
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-lg-4">
              <div className="experience-block">
                <div className="number">+30</div>
                <p className="label">anos de experiência</p>
              </div>
            </div>
            <div className="col-lg-8">
              <h2>Dentistas com Mais de 30 Anos de Experiência em Barueri</h2>
              <p>
                Experiência clínica faz diferença no diagnóstico e no
                planejamento odontológico.
              </p>
              <p>
                A equipe da nossa clínica odontológica no Bethaville possui mais
                de 30 anos de experiência para avaliar cada caso com precisão e
                indicar o melhor tratamento para cada paciente.
              </p>
              <a
                href={WHATSAPP_LINK}
                rel="noopener noreferrer"
                target="_blank"
                className="btn-authority"
                title="Agendar Avaliação"
              >
                <FiCheck size={18} />
                Agendar Avaliação
              </a>
            </div>
          </div>
        </div>
      </AuthoritySection>

      {/* ── FAQ (AEO) ── */}
      <FaqSection>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-9">
              <h2>
                Perguntas Frequentes sobre Dentista no Bethaville, Barueri
              </h2>
              {FAQS.map((faq) => (
                <div key={faq.question} className="faq-item">
                  <h3>{faq.question}</h3>
                  <p>{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </FaqSection>

      {/* ── CTA FINAL ── */}
      <CtaFinal>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <h2>Agende Sua Avaliação Completa no Bethaville</h2>
              <p>
                Avaliação odontológica com tecnologia avançada e diagnóstico
                preciso em Bethaville, Barueri.
              </p>
              <a
                href={WHATSAPP_LINK}
                rel="noopener noreferrer"
                target="_blank"
                className="cta-btn"
                title="Agendar Agora"
              >
                Agendar Agora <img src={whatsAppSVG} width={22} alt="" />
              </a>
              <small>*Não atendemos convênios e Planos de Saúde</small>
              <address className="nap">
                <strong>COI Prime Odontologia</strong>
                <br />
                {STREET_ADDRESS}
                <br />
                Barueri - SP, CEP {POSTAL_CODE}
                <br />
                Tel.:{' '}
                <a
                  href={`tel:${PHONE_E164}`}
                  title={`Telefone: ${PHONE_LABEL}`}
                >
                  {PHONE_LABEL}
                </a>
                <br />
                <a
                  href={MAPS_LINK}
                  rel="noopener noreferrer"
                  target="_blank"
                  title="Ver a COI Prime no Google Maps"
                >
                  Ver no Google Maps
                </a>
              </address>
            </div>
          </div>
        </div>
      </CtaFinal>
    </Container>
  );
};

export default DentistaBarueri;
